import { TimePicker, Flex, Form } from "antd"
import { QuestionCircleOutlined } from "@ant-design/icons"
import { useEffect, useRef, useState } from "react";
import { getActiveSetValue, getConditionalRevertValue, shouldRevertConditionalValue } from "../../../utils/formsHelper";
import { safeDayjs, toDatePickerValue } from "../../../utils/safeDate.js";
import dayjs from "dayjs"


const TimeInput = (props) => {
    const { 
        label, 
        placeholder, 
        tooltip, 
        helpText, 
        mandatory, 
        readOnly, 
        timeFormat, 
        showSeconds,
        use12Hours,
        allowClear,
        showNow,
        realTime,
        minuteStep,
        hourStep,
        secondStep,
        currentTime: currentTimeEnabled = false,
        id,
        form,
        previewMode,
        formValues,
        setValueData
    } = props
    
    const [currentTime, setCurrentTime] = useState(null);
    
    const rules = mandatory ? [{ required: true }] : undefined;
    const disabled = previewMode && readOnly ? true : false
    const help = tooltip ? { title: helpText, icon: <QuestionCircleOutlined /> } : undefined;
    
    // Determine time format based on props
    let currentTimeFormat = timeFormat || 'HH:mm';
    if (showSeconds && !currentTimeFormat.includes('ss')) {
        if (use12Hours) {
            currentTimeFormat = 'hh:mm:ss A';
        } else {
            currentTimeFormat = 'HH:mm:ss';
        }
    }

    let activeSetValue = getActiveSetValue(setValueData)

    // A stored time can be an ISO string, a bare "HH:mm", a live dayjs, or a dayjs
    // that lost its prototype on a round trip - toDatePickerValue handles all of
    // them and returns null for anything the picker cannot show.
    const toDayjsValue = (value) => toDatePickerValue(value, currentTimeFormat)

    // The time the condition last wrote, so it can be taken back once the condition
    // stops passing without discarding a time the user picked since.
    const appliedFormValueRef = useRef(undefined)

    // Real-time update effect
    useEffect(() => {
        if (realTime) {
            const interval = setInterval(() => {
                const now = dayjs();
                setCurrentTime(now);
                if (!disabled) {
                    form?.setFieldValue(id, now);
                }
            }, 1000);
            
            return () => clearInterval(interval);
        }
    }, [realTime, disabled, form, id]);

    useEffect(() => {
        if (activeSetValue?.value) {
            let timeValue = safeDayjs(activeSetValue.value, currentTimeFormat)
            form?.setFieldValue(id, timeValue)
            appliedFormValueRef.current = timeValue
            return
        }
        if (!currentTimeEnabled && shouldRevertConditionalValue(activeSetValue, { disabled, currentValue: toDayjsValue(form?.getFieldValue(id)), appliedValue: appliedFormValueRef.current })) {
            const fallback = getConditionalRevertValue(id, props.defaultFormValues)
            form?.setFieldValue(id, fallback != null ? toDayjsValue(fallback) : null)
            appliedFormValueRef.current = undefined
            return
        }
        appliedFormValueRef.current = undefined
        if (currentTimeEnabled && previewMode && !formValues?.[id]) {
            form?.setFieldValue(id, dayjs())
        }
    }, [activeSetValue, form, id, disabled, currentTimeEnabled, previewMode, formValues?.[id]])

    return (
        <div className="form-element">
            <Flex vertical>
                <Form.Item
                    name={id}
                    label={label}
                    tooltip={help}
                    rules={rules}
                    disabled={disabled}
                    getValueProps={(value) => ({ value: toDayjsValue(value) })}
                >
                    <TimePicker 
                        size="large" 
                        placeholder={placeholder || ""} 
                        format={currentTimeFormat} 
                        use12Hours={use12Hours || currentTimeFormat.includes('A')} 
                        disabled={disabled} 
                        style={{ width: "100%" }}
                        allowClear={allowClear !== false}
                        showNow={showNow !== false}
                        minuteStep={minuteStep || 1}
                        hourStep={hourStep || 1}
                        secondStep={secondStep || 1}
                        value={realTime ? currentTime : undefined}
                    />
                </Form.Item>
            </Flex>
        </div>
    )
}

export default TimeInput
